// Which Custom API providers this agent pushes its captured leads to, with what fixed values.
//
// Stored in the agent's config_json under `custom_api_push` — the one key
// CustomApiPushService reads:
//   {"providerId": "<CUSTOM_API row id>", "values": {"issueType": "ACCOUNT_HACKED", "language": "sn"}}
// (or an array of them).
//
// <h3>Why this has its own Save</h3>
// PUT /agents/{id} REPLACES config_json with the body and re-pushes the agent to
// the runtime. So a binding save that sent anything but the agent's whole current
// document would wipe what it left out, and one that sent the editor's in-memory
// copy would also send every other unsaved edit on this page — and a stale
// runtime_document with them, which the next push overlays onto (the
// "agent save stores the wrong document" trap).
//
// So it reads the agent fresh, changes ONLY `custom_api_push`, compares the whole
// document it is about to send against the one it read — every leaf, not a chosen
// few — and refuses if anything else differs. Then it hands the parent back just
// the binding and the server-owned fields the push changed.
import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Stethoscope, Loader2, CheckCircle2, XCircle, AlertTriangle, Save } from 'lucide-react';
import { aiAgentsService, AiAgent } from 'api/services/aiAgents.service';
import {
  crmProvidersService, customApiService, CrmProvider, CustomApiBindingCheck,
} from 'api/services/crmProviders.service';
import { useAuth } from 'hooks/useAuth';
import {
  readBindings, writeBindings, bindingProblems, withPushBinding, changesOutsideBinding,
  modeOf, PushBindings, KV, CUSTOM_API_CHECK_ROLES,
} from 'pages/Crm/customApiShapes';

const cls = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none text-sm';
const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** Fields the PUT changes server-side, merged back so the page's next Save does not undo them. */
const SERVER_OWNED: (keyof AiAgent)[] = ['runtime_document', 'runtime_synced', 'runtime_message', 'wsurl', 'agent_uuid', 'updated_at'];

export default function CustomApiPushSection({ agent, onSaved }: {
  agent: AiAgent;
  /** Receives ONLY custom_api_push and the server-owned fields; the page's other edits stay as they are. */
  onSaved: (patch: Partial<AiAgent>) => void;
}) {
  const { currentOrganization, user } = useAuth();
  const role = (currentOrganization?.role || user?.role || '').toLowerCase();
  const canCheck = CUSTOM_API_CHECK_ROLES.includes(role);

  const [state, setState] = useState<PushBindings>(() => readBindings(agent.custom_api_push));
  // What the binding was when this section last read or wrote it. A save refuses
  // if the server's copy has moved since — somebody else edited it.
  const [base, setBase] = useState<unknown>(agent.custom_api_push);
  const [providers, setProviders] = useState<CrmProvider[]>([]);
  const [providersError, setProvidersError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [check, setCheck] = useState<CustomApiBindingCheck[] | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      // The AGENT's organisation, named explicitly: the binding must point at a
      // provider there, and the portal may be looking at a different one.
      const res = await crmProvidersService.list(agent.organization_id);
      if (res.success) setProviders((res.data || []).filter(p => p.providerType === 'CUSTOM_API'));
      else setProvidersError(res.error || 'Could not load this organisation’s Custom API providers');
    })();
  }, [agent.organization_id]);

  const next = useMemo(() => writeBindings(state), [state]);
  const dirty = !same(next, base);
  const problems = bindingProblems(state);

  const setBinding = (i: number, patch: Partial<PushBindings['bindings'][number]>) =>
    setState(s => ({ ...s, bindings: s.bindings.map((b, j) => (j === i ? { ...b, ...patch } : b)) }));
  const setValues = (i: number, values: KV[]) => setBinding(i, { values });

  const save = async () => {
    if (agent.id == null || problems.length) return;
    setSaving(true); setError(null); setNote(null);
    try {
      const fresh = await aiAgentsService.get(agent.id);
      if (!fresh.success || !fresh.data) { setError(fresh.message || fresh.error || 'Could not re-read the agent'); return; }
      if (!same(fresh.data.custom_api_push, base)) {
        setError('This agent’s Custom API binding was changed elsewhere since this page loaded. Reload the page and redo the change.');
        return;
      }
      const body = withPushBinding(fresh.data, next);
      const outside = changesOutsideBinding(fresh.data, body);
      if (outside.length) {
        // Cannot happen by construction; if it ever does, sending would change
        // more than the binding, which is exactly what this save promises not to.
        setError(`Refused: the save would also change ${outside.slice(0, 5).join(', ')}${outside.length > 5 ? '…' : ''}.`);
        return;
      }
      const res = await aiAgentsService.update(agent.id, body);
      if (!res.success || !res.data) { setError(res.message || res.error || 'Failed to save the binding'); return; }
      const saved = res.data;
      const patch: Partial<AiAgent> = { custom_api_push: saved.custom_api_push };
      SERVER_OWNED.forEach(k => { if (k in saved) (patch as any)[k] = saved[k]; });
      setBase(saved.custom_api_push);
      setState(readBindings(saved.custom_api_push));
      setCheck(null);
      onSaved(patch);
      setNote(`Saved.${saved.runtime_message ? ` ${saved.runtime_message}.` : ''}`);
    } finally {
      setSaving(false);
    }
  };

  const runCheck = async () => {
    if (agent.id == null || !agent.organization_id) return;
    setChecking(true); setCheckError(null);
    const res = await customApiService.check(agent.id, agent.organization_id);
    setChecking(false);
    if (res.success && res.data) setCheck(res.data.bindings);
    else { setCheck(null); setCheckError(res.error || 'Check failed'); }
  };

  const nameOf = (id: string) => providers.find(p => p.id === id)?.name || id;

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-gray-400">
        After this agent captures a lead, cpass sends it to each provider bound here — once per call, beside
        whatever CRM the business has. The request itself (URL, body template, auth) is set up under
        Booking &amp; CRM → Custom API. Values below fill that template’s <code>{'{{agent.*}}'}</code>.
      </p>
      {agent.crm_sync_disabled && (
        <p className="text-[11px] text-amber-600 dark:text-amber-400 flex gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
          CRM sync is switched off for this agent, so nothing is pushed — bindings are kept but each push is recorded as local only.
        </p>
      )}
      {agent.post_call_lead_mode === 'OFF' && state.bindings.length > 0 && (
        <p className="text-[11px] text-amber-600 dark:text-amber-400 flex gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
          Lead recovery after the call is Never — a caller who hangs up before the agent files the lead is never pushed.
        </p>
      )}
      {providersError && <p className="text-[11px] text-red-600">{providersError}</p>}

      {state.bindings.map((b, i) => {
        const known = providers.find(p => p.id === b.providerId);
        return (
          <div key={i} className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
            <div className="flex items-end gap-2">
              <label className="flex-1 block">
                <span className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Provider</span>
                <select className={cls} value={b.providerId} onChange={e => setBinding(i, { providerId: e.target.value })}>
                  <option value="">Pick a Custom API provider…</option>
                  {b.providerId && !known && <option value={b.providerId}>{b.providerId} (not in this organisation)</option>}
                  {providers.map(p => {
                    const fetchTool = modeOf(p.configJson) === 'FETCH';
                    return (
                      <option key={p.id} value={p.id} disabled={fetchTool}>
                        {p.name || p.id}{fetchTool ? ' — in-call lookup, not a push' : ''}{p.enabled === false ? ' (disabled)' : ''}
                      </option>
                    );
                  })}
                </select>
              </label>
              <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 pb-2.5">
                <input type="checkbox" checked={b.enabled} onChange={e => setBinding(i, { enabled: e.target.checked })} /> On
              </label>
              <button type="button" className="tl-icon-btn mb-1" title="Remove this binding"
                      onClick={() => setState(s => ({ ...s, bindings: s.bindings.filter((_, j) => j !== i) }))}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            {known && known.enabled === false && (
              <p className="text-[11px] text-amber-600">That provider is disabled — nothing is sent until it is enabled.</p>
            )}
            <div>
              <span className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Fixed values</span>
              <div className="space-y-1.5">
                {b.values.map((v, k) => (
                  <div key={k} className="flex gap-1.5">
                    <input className={cls + ' font-mono text-xs'} placeholder="issueType" value={v.key}
                           onChange={e => setValues(i, b.values.map((x, j) => (j === k ? { ...x, key: e.target.value } : x)))} />
                    <input className={cls + ' font-mono text-xs'} placeholder="ACCOUNT_HACKED" value={v.value}
                           onChange={e => setValues(i, b.values.map((x, j) => (j === k ? { ...x, value: e.target.value } : x)))} />
                    <button type="button" className="tl-icon-btn" title="Remove"
                            onClick={() => setValues(i, b.values.filter((_, j) => j !== k))}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button type="button" className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-1"
                        onClick={() => setValues(i, [...b.values, { key: '', value: '' }])}>
                  <Plus className="w-3 h-3" /> Add value
                </button>
              </div>
            </div>
          </div>
        );
      })}
      {state.opaque.length > 0 && (
        <p className="text-[11px] text-gray-400">
          {state.opaque.length} entr{state.opaque.length === 1 ? 'y' : 'ies'} in the stored binding cannot be read as a binding; kept as they are.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className="border rounded px-3 py-1.5 text-sm inline-flex items-center gap-1"
                onClick={() => setState(s => ({ ...s, bindings: [...s.bindings, { providerId: '', values: [], enabled: true, hadEnabled: false, extra: {} }] }))}>
          <Plus className="w-3.5 h-3.5" /> Bind a provider
        </button>
        <button type="button" className="tl-btn tl-btn-primary tl-btn-sm disabled:opacity-50"
                disabled={!dirty || saving || problems.length > 0} onClick={save}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {saving ? 'Saving…' : 'Save binding'}
        </button>
        {dirty && !saving && (
          <button type="button" className="text-xs text-gray-500 hover:underline"
                  onClick={() => { setState(readBindings(base)); setError(null); }}>
            Discard
          </button>
        )}
        {canCheck && (
          <button type="button" className="border rounded px-3 py-1.5 text-sm inline-flex items-center gap-1 disabled:opacity-50"
                  disabled={checking || !agent.organization_id} onClick={runCheck}
                  title="Resolves the SAVED binding: provider exists here, is enabled, is a push, and its config parses. Sends nothing.">
            {checking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Stethoscope className="w-3.5 h-3.5" />} Check
          </button>
        )}
        {dirty && <span className="text-[11px] text-amber-600">Unsaved — Check reads the saved binding.</span>}
      </div>
      <p className="text-[11px] text-gray-400">
        Save binding changes only this agent’s <code>custom_api_push</code>, re-read fresh from the server — your other
        unsaved edits on this page are not sent with it. Like every agent save, it re-sends the agent to the runtime.
      </p>
      {problems.length > 0 && <ul className="text-[11px] text-red-600 list-disc ml-5">{problems.map(p => <li key={p}>{p}</li>)}</ul>}
      {error && <p className="text-[12px] text-red-600">{error}</p>}
      {note && <p className="text-[12px] text-green-700 dark:text-green-400">{note}</p>}

      {checkError && <p className="text-[12px] text-red-600">{checkError}</p>}
      {check && (
        check.length === 0 ? (
          <p className="text-[12px] text-gray-500">No enabled binding is saved on this agent — nothing would be pushed.</p>
        ) : (
          <ul className="space-y-1.5">
            {check.map((c, i) => (
              <li key={i} className="text-[12px] flex gap-2 items-start">
                {c.usable
                  ? <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-px" />
                  : <XCircle className="w-4 h-4 text-red-600 shrink-0 mt-px" />}
                <span>
                  <span className="font-medium">{nameOf(c.providerId)}</span>{' '}
                  {c.usable
                    ? <>usable · <code className="break-all">{c.url}</code>{c.placeholders && c.placeholders.length > 0 && <> · uses {c.placeholders.join(', ')}</>}</>
                    : <span className="text-red-700 dark:text-red-300">{c.problem}</span>}
                </span>
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  );
}
